import { metadataOnlyQuery, queryClient } from "@istari-frontend/api-client";
import { V3Models } from "@istari/istari-client";
import { useMutation } from "@tanstack/react-query";

import { modelQueryKeys } from "~/api/model/modelQueryKeys";
import { resourceQueryKeys } from "~/resource/data-source/api/resource-query-options";

import { announceUpload } from "./upload-announcements";
import { registerUploadPayload, runUploadPayload, type UploadIntent } from "./upload-payloads";
import { commitResource, createUploadRevision } from "./upload-resource-operation";
import {
  assertUploadSessionCurrent,
  awaitUploadSessionReady,
  captureUploadSession,
  isUploadSessionCurrent,
  type UploadSession,
  uploadSessionCallbacks,
} from "./upload-session-callbacks";

type CreateResourceParams = {
  file: File;
  intent?: UploadIntent;
  displayName: string;
  description?: string;
  externalIdentifier?: string;
  resourceType: V3Models.ResourceCreateTypeDto;
};

type CreateResourceOperation = Omit<CreateResourceParams, "file" | "intent"> & { operationId: string };
type OwnedOperation = CreateResourceOperation & { session: UploadSession };

export function useCreateResource() {
  const mutation = useMutation({
    mutationFn: (params: OwnedOperation) => {
      assertUploadSessionCurrent(params.session);
      return runUploadPayload(params.operationId, async (file, context) => {
        await awaitUploadSessionReady(params.session);
        const revision = await createUploadRevision(file, context, params);
        await awaitUploadSessionReady(params.session);
        return commitResource(revision, params.resourceType, context);
      });
    },
    retry: false,
    onSuccess: (_data, variables) => {
      if (!isUploadSessionCurrent(variables.session)) return;
      void queryClient.invalidateQueries({ queryKey: resourceQueryKeys.all(), predicate: metadataOnlyQuery });
      void queryClient.invalidateQueries({ queryKey: modelQueryKeys.all, predicate: metadataOnlyQuery });
      void queryClient.invalidateQueries({ queryKey: ["modelsList"], predicate: metadataOnlyQuery });
    },
    meta: { triggerGlobalInvalidation: false },
  });
  const toOperation = ({ file, intent, ...metadata }: CreateResourceParams): CreateResourceOperation => {
    const operation = { ...metadata, operationId: registerUploadPayload(file, undefined, intent) };
    if (intent !== "generated") announceUpload({ operationId: operation.operationId, name: file.name });
    return operation;
  };
  const submit = (params: CreateResourceOperation, options?: Parameters<typeof mutation.mutateAsync>[1]) => {
    const session = captureUploadSession();
    return mutation.mutateAsync({ ...params, session }, uploadSessionCallbacks(options, session));
  };
  return {
    ...mutation,
    mutate: (params: CreateResourceParams, options?: Parameters<typeof mutation.mutate>[1]) => {
      const session = captureUploadSession();
      assertUploadSessionCurrent(session);
      mutation.mutate({ ...toOperation(params), session }, uploadSessionCallbacks(options, session));
    },
    mutateAsync: (params: CreateResourceParams, options?: Parameters<typeof mutation.mutateAsync>[1]) => {
      const session = captureUploadSession();
      assertUploadSessionCurrent(session);
      return mutation.mutateAsync({ ...toOperation(params), session }, uploadSessionCallbacks(options, session));
    },
    mutateOperationAsync: submit,
  };
}
